import { Skeleton, Switch, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useMessagingSettingsQuery, useUpdateMessagingSettings } from '../../queries/messagingSettings';
import { CATEGORY_META } from './constants';
import { Cols2, ListRow, Muted, Panel, PanelTitle, RowSub, RowTitle, RowValue } from './styled';

const TOGGLES = [
  {
    key: 'draftAutomatically',
    title: 'Draft replies automatically',
    sub: 'Agent Zero writes a suggested reply as soon as a message comes in.'
  },
  {
    key: 'requireApproval',
    title: 'Require approval before sending',
    sub: 'Nothing goes out until someone hits Use. Recommended.',
    locked: true
  },
  { key: 'learnFromEdits', title: 'Learn from edits', sub: 'When you edit a suggestion, the change shapes future drafts.' },
  {
    key: 'suggestDuringQuietHours',
    title: 'Suggest during quiet hours',
    sub: 'Keep drafting overnight so replies are queued for the morning.'
  },
  {
    key: 'autoSendEmergencyAck',
    title: 'Auto-send emergency acknowledgements',
    sub: 'Send the acknowledgement template instantly on emergency-tier calls.'
  }
];

export default function SmartReplyTab({ canManage }) {
  const { data, isLoading } = useMessagingSettingsQuery();
  const update = useUpdateMessagingSettings({ success: 'Smart Reply updated' });
  const smartReply = data?.settings?.smartReply || {};
  const learned = data?.learned || [];

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  return (
    <Cols2>
      <Panel aria-labelledby="smart-behaves">
        <PanelTitle>
          <h2 id="smart-behaves">How Smart Reply behaves</h2>
        </PanelTitle>
        {TOGGLES.map((t) => (
          <ListRow key={t.key}>
            <div>
              <RowTitle id={`sr-${t.key}`}>
                {t.title}
                {t.locked && (
                  <Tooltip title="Kept on for safety">
                    <FontAwesomeIcon icon={faLock} aria-label="Locked" />
                  </Tooltip>
                )}
              </RowTitle>
              <RowSub>{t.sub}</RowSub>
            </div>
            <Switch
              aria-labelledby={`sr-${t.key}`}
              checked={Boolean(smartReply[t.key])}
              disabled={!canManage || t.locked}
              onChange={(value) => update.mutate({ smartReply: { [t.key]: value } })}
            />
          </ListRow>
        ))}
      </Panel>

      <Panel aria-labelledby="smart-learned">
        <PanelTitle>
          <h2 id="smart-learned">What it has learned</h2>
          <span>from your edits and sends</span>
        </PanelTitle>
        {learned.length ? (
          learned.map((l) => (
            <ListRow key={l.label}>
              <div>
                <RowTitle>{l.label}</RowTitle>
                <RowSub>{(CATEGORY_META[l.category] || CATEGORY_META.general).label}</RowSub>
              </div>
              <RowValue>
                <b>{l.confidence}%</b>
                <Muted $small>{l.replies} replies</Muted>
              </RowValue>
            </ListRow>
          ))
        ) : (
          <ListRow>
            <div>
              <RowTitle>Nothing learned yet</RowTitle>
              <RowSub>Patterns show up here as you send replies and use templates.</RowSub>
            </div>
          </ListRow>
        )}
      </Panel>
    </Cols2>
  );
}
